import { useEffect, useMemo, useState } from 'react';

const MIN_PASSWORD_LENGTH = 8;
export const PASSWORD_RULE_COUNT = 5;

interface PasswordRule {
    id: string;
    label: string;
    valid: boolean;
}

function passwordRules(password: string): PasswordRule[] {
    return [
        {
            id: 'length',
            label: 'At least 8 characters',
            valid: password.length >= MIN_PASSWORD_LENGTH
        },
        { id: 'upper', label: 'At least one uppercase letter', valid: /[A-Z]/.test(password) },
        { id: 'lower', label: 'At least one lowercase letter', valid: /[a-z]/.test(password) },
        { id: 'number', label: 'At least one number', valid: /[0-9]/.test(password) },
        {
            id: 'special',
            label: 'At least one special character',
            valid: /[^A-Za-z0-9]/.test(password)
        }
    ];
}

function strengthColorFor(score: number): string {
    if (score <= 1) return 'bg-red-500';
    if (score === 2) return 'bg-orange-500';
    if (score === 3) return 'bg-amber-500';
    if (score === 4) return 'bg-lime-500';
    return 'bg-green-600';
}

export function usePasswordForm(isOpen: boolean) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowCurrent(false);
            setShowNew(false);
            setShowConfirm(false);
        }
    }, [isOpen]);

    const rules = useMemo(() => passwordRules(newPassword), [newPassword]);
    const score = rules.filter((r) => r.valid).length;
    const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

    return {
        currentPassword,
        setCurrentPassword,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        showCurrent,
        setShowCurrent,
        showNew,
        setShowNew,
        showConfirm,
        setShowConfirm,
        rules,
        score,
        strengthColor: strengthColorFor(score),
        passwordsMatch,
        allValid: score === PASSWORD_RULE_COUNT && passwordsMatch
    };
}
